import PropTypes from 'prop-types';
import React, {createContext, useContext, useEffect, useMemo} from 'react';
import {EventType} from '@azure/msal-browser';
import {useMsal} from "@azure/msal-react";
// import { useFeatureFlags } from './FeatureFlagProvider'
import {useAppShellContext} from './AppShellContext';
import {useSessionContext} from './session-context';
import {Auth} from './Auth';
import {localStorage, sessionStorage} from '../storage';
import {msalConfig} from '../msal/msalConfig';
import useLogFeError from './error/useLogFeError';
 
const AuthContext = createContext()
 
function useAuth() {
  return useContext(AuthContext);
}
 
const createAuthInstance = (config, msal, sessionContext, isCipMuleActive, logError) => new Auth({
    cache: config.main.sessionStorageType === 'session' ? sessionStorage : localStorage,
    sessionContext,
    msal,
    cip: config.cip,
    contentful: config.contentful,
    accessTokenValidityThresholdMs: msalConfig.system.tokenRenewalOffsetSeconds * 1000,
    mainWindowRedirectUri: process.env.AAD_REDIRECT_URL,
    debug: false,
    isLocal: config.cip.isLocal,
    apiUrl: config.graphql.baseUri,
    apiWsUrl: config.graphql.baseWsUri,
    batch: config.graphql.batch,
    authFailureRedirectPath: '/auth/error',
    genericApiFailureRedirectPath: '/content/error',
    isCipMuleActive,
    logError
  })
const AuthProvider = ({ children}) => {
  const {config} = useAppShellContext();
  const sessionContext = useSessionContext()
  const msal = useMsal();
  // const featureFlag = useFeatureFlags();
  const isCipMuleActive = false; // featureFlag.isActive('cipMulesoftMigration');
  const logError = useLogFeError(false);
  const auth = useMemo(() => createAuthInstance(config, msal, sessionContext, isCipMuleActive, logError), [config, msal, sessionContext, isCipMuleActive, logError])

  // 🔧 FIX: Subscribe to MSAL auth events and always call setActiveAccount after any
  // LOGIN_SUCCESS / ACQUIRE_TOKEN_SUCCESS / SSO_SILENT_SUCCESS event.
  // This is the MSAL-recommended pattern and is the root fix for getActiveAccount()
  // returning null during token refresh when the active-account pointer key is gone.
  useEffect(() => {
    const { instance } = msal;
    const callbackId = instance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        event.eventType === EventType.SSO_SILENT_SUCCESS
      ) {
        const account = event.payload?.account;
        if (account && instance.setActiveAccount) {
          instance.setActiveAccount(account);
        }
      }
    });
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msal.instance]);

  // 🔧 FIX: Restore active-account pointer on every accounts-list change.
  // Covers: hard refresh, new tab, and any case where the pointer key was deleted
  // but MSAL's token cache (getAllAccounts) is still intact in localStorage.
  useEffect(() => {
    const { instance, accounts } = msal;
    if (accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msal.accounts, msal.instance]);

  // 🔧 CRITICAL: Expose Auth instance globally for MSAL redirect handling
  // applications.js waits for this to call gqlAuthCallbackMSAL after SSO redirect
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line no-underscore-dangle
    window.__AUTH_INSTANCE__ = auth;
    // eslint-disable-next-line no-underscore-dangle
    if (!window.__AUTH_INSTANCE_SET_LOGGED__) {
      // eslint-disable-next-line no-underscore-dangle
      window.__AUTH_INSTANCE_SET_LOGGED__ = true;
    }
  }
 
  return (
      <AuthContext.Provider value={auth}>
        {children}
      </AuthContext.Provider>
  );
}
 
AuthProvider.propTypes = {
  children: PropTypes.node
}
 
export {AuthProvider, useAuth}
 